import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Mail, 
  Search,
  X,
  Paperclip,
  Eye,
  ChevronLeft,
  ChevronRight,
  Inbox,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getAllTemplates,
  deleteTemplate,
  EmailTemplate,
} from '../../services/emailTemplate_service';
import EmailTemplateForm from './EmailTemplateForm';
import EmailTemplatePreview from './EmailTemplatePreview';
import LoadingSpinner from '../LoadingSpinner';
import '../styles/EmailTemplatesManager.css';

export default function EmailTemplatesManager() {
  // États
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });

  // Modales
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [templateToDelete, setTemplateToDelete] = useState<{ id: number; name: string } | null>(null);

  const formatDate = (dateString: string) => {
    try {
      const isoDate = dateString.replace(' ', 'T');
      const date = new Date(isoDate);
      
      if (isNaN(date.getTime())) return 'Date inconnue';
      
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return 'Date inconnue';
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Charger les templates
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllTemplates(pagination.page, pagination.limit);
      setTemplates(response.data);
      setPagination(response.pagination);
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors du chargement des templates');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates, debouncedSearchTerm]);

  // Gestionnaires d'événements
  const handleCreateClick = () => {
    setSelectedTemplate(null);
    setIsFormModalOpen(true);
  };

  const handleEditClick = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setIsFormModalOpen(true);
  };

  const handlePreviewClick = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setIsPreviewModalOpen(true);
  };

  const handleDeleteClick = (template: EmailTemplate) => {
    setTemplateToDelete({ id: template.id, name: template.name });
    setIsDeleteModalOpen(true);
  };

  const handleDuplicateClick = (template: EmailTemplate) => {
    setSelectedTemplate({
      ...template,
      id: 0,
      name: `${template.name} (copie)`
    });
    setIsFormModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;
    
    try {
      await deleteTemplate(templateToDelete.id);
      toast.success('Template supprimé avec succès');
      fetchTemplates();
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de la suppression');
    } finally {
      setIsDeleteModalOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleFormSuccess = () => {
    fetchTemplates();
    setIsFormModalOpen(false);
    setSelectedTemplate(null);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const changePage = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  return (
    <div className="templates-container">
      <div className="templates-content">
        {/* Header */}
        <div className="templates-header">
          <div className="header-left">
            <div className="header-icon">
              <Mail size={24} />
            </div>
            <div className="header-title">
              <h1>Templates d'emails</h1>
              <p>{pagination.total} template{pagination.total > 1 ? 's' : ''} disponibles</p>
            </div>
          </div>

          <button
            onClick={handleCreateClick}
            className="btn-primary1"
          >
            <Plus size={18} />
            Nouveau template
          </button>
        </div>

        {/* Barre de recherche */}
        <div className="search-container">
          <div className="search-wrapper">
            <Search className="search-icon" size={18} />
            <input
              type="text"
              placeholder="Rechercher un template par nom, sujet..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button onClick={clearSearch} className="clear-search" title="Effacer"> 
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        {/* Liste des templates - Version Grille */}
        {loading ? (
          <div className="loading-container">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="templates-list">
            {templates.length === 0 ? (
              <div className="empty-state">
                <Inbox className="empty-icon" />
                <p>Aucun template trouvé</p>
                <button
                  onClick={handleCreateClick}
                  className="btn-outline"
                >
                  Créer votre premier template
                </button>
              </div>
            ) : (
              <>
                <div className="templates-grid">
                  {templates.map((template) => (
                    <div key={template.id} className="template-card">
                      {/* En-tête de la carte */}
                      <div className="template-card-header">
                        <div className="template-icon">
                          <Mail size={20} />
                        </div>
                        <h3 className="template-name">{template.name}</h3>
                        {template.attachments && template.attachments.length > 0 && (
                          <span className="template-attachments">
                            <Paperclip size={12} />
                            {template.attachments.length}
                          </span>
                        )}
                      </div>

                      {/* Corps de la carte */}
                      <div className="template-card-body">
                        <div className="template-subject">
                          <div className="template-subject-label">Sujet</div>
                          <div className="template-subject-value">
                            {template.subject}
                          </div>
                        </div>

                        {template.description && (
                          <p className="template-description">
                            {template.description}
                          </p>
                        )}

                        <div className="template-meta">
                          {template.creator && (
                            <span className="template-creator">
                              <Mail size={12} />
                              {template.creator.name} {template.creator.firstname}
                            </span>
                          )}
                          <span className="template-date">
                            <Mail size={12} />
                            {formatDate(template.created_at)}
                          </span>
                        </div>
                      </div>

                      {/* Pied de carte avec actions */}
                      <div className="template-card-footer">
                        <button
                          onClick={() => handlePreviewClick(template)}
                          className="btn-action btn-action-preview"
                          title="Aperçu"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleEditClick(template)}
                          className="btn-action btn-action-edit"
                          title="Modifier"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(template)}
                          className="btn-action btn-action-danger"
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {templates.length > 0 && (
                  <div className="pagination">
                    <button
                      onClick={() => changePage(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="btn-pagination"
                    >
                      <ChevronLeft size={16} />
                      Précédent
                    </button>
                    <span className="pagination-info">
                      Page {pagination.page} sur {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => changePage(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                      className="btn-pagination"
                    >
                      Suivant
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Modale de confirmation de suppression personnalisée */}
        {isDeleteModalOpen && templateToDelete && (
          <div className="modal-overlay" onClick={() => setIsDeleteModalOpen(false)}>
            <div className="delete-modal" onClick={e => e.stopPropagation()}>
              <div className="delete-modal-header">
                <div className="delete-icon">
                  <AlertTriangle size={24} />
                </div>
                <h3>Confirmer la suppression</h3>
              </div>
              
              <div className="delete-modal-body">
                <p>
                  Êtes-vous sûr de vouloir supprimer le template <strong>"{templateToDelete.name}"</strong> ?
                </p>
                <p className="delete-warning">
                  Cette action est irréversible et pourrait affecter les emails utilisant ce template.
                </p>
              </div>

              <div className="delete-modal-footer">
                <button
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setTemplateToDelete(null);
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="btn-danger"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modales existantes */}
        {isFormModalOpen && (
          <EmailTemplateForm
            template={selectedTemplate}
            onClose={() => {
              setIsFormModalOpen(false);
              setSelectedTemplate(null);
            }}
            onSuccess={handleFormSuccess}
          />
        )}

        {isPreviewModalOpen && selectedTemplate && (
          <EmailTemplatePreview
            template={selectedTemplate}
            onClose={() => {
              setIsPreviewModalOpen(false);
              setSelectedTemplate(null);
            }}
          />
        )}
      </div>
    </div>
  );
}